import { useMemo, useState } from 'react';
import { Check, Plus, Search, X } from 'lucide-react';
import type { Task } from '../../../lib/tasksApi';
import { useLabels, useCreateProjectLabel } from '../../../hooks/useLabels';
import { useAddTaskLabel, useRemoveTaskLabel } from '../../../hooks/useTaskDetail';
import { Popover } from './Popover';
import { LinearLabelPill } from './LinearLabelPill';

/**
 * CLE-182 — label add/remove control for the task detail sidebar.
 *
 * Shows the task's current labels as colored pills (click to remove) plus a
 * "+ Add label" trigger that opens a searchable popover of the shared label
 * catalogue, with inline create-on-the-fly scoped to the task's project.
 */

const SWATCHES = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
];

/**
 * `pill` is the desktop sidebar's small rounded chip. `chip` matches the
 * property chips of the phone layout (MobileProperties): same 30px height,
 * same rounded-md corners, same 12px text — so the label row does not read
 * as a different control from the status/priority/assignee row above it.
 */
export type LabelEditorVariant = 'pill' | 'chip';

const LABEL_CLASS: Record<LabelEditorVariant, string> = {
  pill: 'rounded-full px-2 py-px text-[11px]',
  chip: 'min-h-[30px] rounded-md px-2 py-1 text-[12px]',
};
const ADD_CLASS: Record<LabelEditorVariant, string> = {
  pill: 'rounded-full px-2 py-0.5 text-[11px]',
  chip: 'min-h-[30px] rounded-md px-2 py-1 text-[12px]',
};

export function LabelEditor({
  task,
  variant = 'pill',
}: {
  task: Task;
  variant?: LabelEditorVariant;
}) {
  const addLabel = useAddTaskLabel(task.id);
  const removeLabel = useRemoveTaskLabel(task.id);
  const chip = variant === 'chip';

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {task.labels.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => removeLabel.mutate(l.id)}
          title={`Elimină ${l.name}`}
          className={`group/lbl inline-flex items-center gap-1.5 border border-gray-300 font-medium text-gray-700 transition-colors hover:border-red-500/60 hover:text-red-500 ${LABEL_CLASS[variant]}`}
          style={chip ? undefined : { paddingTop: 1, paddingBottom: 1 }}
        >
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: l.color }}
          />
          {l.name}
          <X size={10} className="opacity-0 group-hover/lbl:opacity-100" />
        </button>
      ))}

      <Popover
        width={240}
        align="start"
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            className={`inline-flex items-center gap-1 border border-dashed border-gray-300 font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700 ${ADD_CLASS[variant]}`}
          >
            <Plus size={chip ? 13 : 11} /> {task.labels.length === 0 ? 'Adaugă etichetă' : 'Adaugă'}
          </button>
        )}
      >
        {(close) => (
          <LabelMenu
            task={task}
            onAdd={(id) => addLabel.mutate(id)}
            onRemove={(id) => removeLabel.mutate(id)}
            close={close}
          />
        )}
      </Popover>
    </div>
  );
}

function LabelMenu({
  task,
  onAdd,
  onRemove,
  close,
}: {
  task: Task;
  onAdd: (labelId: string) => void;
  onRemove: (labelId: string) => void;
  close: () => void;
}) {
  const { data: labels } = useLabels();
  const createLabel = useCreateProjectLabel(task.projectId);
  const [query, setQuery] = useState('');
  const [color, setColor] = useState(SWATCHES[5]);

  const applied = useMemo(() => new Set(task.labels.map((l) => l.id)), [task.labels]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = labels ?? [];
    return q ? all.filter((l) => l.name.toLowerCase().includes(q)) : all;
  }, [labels, query]);

  const exactMatch = (labels ?? []).some(
    (l) => l.name.toLowerCase() === query.trim().toLowerCase(),
  );
  const canCreate = query.trim().length > 0 && !exactMatch;

  function create() {
    const name = query.trim();
    if (!name) return;
    createLabel.mutate(
      { name, color },
      {
        onSuccess: (label) => {
          onAdd(label.id);
          setQuery('');
        },
      },
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canCreate) create();
          }}
          placeholder="Caută sau creează…"
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>

      <div className="max-h-56 overflow-y-auto py-1">
        {filtered.map((l) => {
          const isOn = applied.has(l.id);
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => (isOn ? onRemove(l.id) : onAdd(l.id))}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: l.color }}
                />
                <span className="truncate">{l.name}</span>
              </span>
              {isOn && <Check size={14} className="text-gray-400" />}
            </button>
          );
        })}
        {filtered.length === 0 && !canCreate && (
          <p className="px-3 py-2 text-sm text-gray-400">Nicio etichetă.</p>
        )}
      </div>

      {canCreate && (
        <div className="border-t border-border px-3 py-2">
          <div className="mb-1.5 flex items-center gap-1">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-4 w-4 rounded-full ${color === c ? 'ring-2 ring-offset-1 ring-offset-[var(--surface-white)]' : ''}`}
                style={{ backgroundColor: c }}
                aria-label={`Culoare ${c}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={create}
            disabled={createLabel.isPending}
            className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-sm text-gray-700 hover:bg-accent disabled:opacity-50"
          >
            <Plus size={14} />
            <span>
              Creează <LinearLabelPill label={{ id: 'new', name: query.trim(), color }} />
            </span>
          </button>
        </div>
      )}

      <div className="border-t border-border py-1">
        <button type="button" onClick={close} className="ui-menu-item text-gray-500">
          Gata
        </button>
      </div>
    </div>
  );
}
